import React, { useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import RamenDiningIcon from "@mui/icons-material/RamenDining";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import SoupKitchenIcon from "@mui/icons-material/SoupKitchen";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CommentIcon from "@mui/icons-material/Comment";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VerifiedIcon from "@mui/icons-material/Verified";

import { withAlpha } from "../../utils/gradients";
import BrandPagination from "./BrandPagination";

/* Paleta de macronutrientes: versión legible de los tokens del sistema
 * (COLORS.carbohidratos/proteinas/lipidos son pasteles muy claros). */
const MACROS = [
  {
    key: "carbohidratos",
    label: "Carbohidratos",
    short: "C",
    color: "#16a34a",
    soft: "rgba(91,240,160,0.16)",
    kcalPerG: 4,
  },
  {
    key: "proteinas",
    label: "Proteínas",
    short: "P",
    color: "#e0574a",
    soft: "rgba(240,138,127,0.16)",
    kcalPerG: 4,
  },
  {
    key: "lipidos",
    label: "Lípidos",
    short: "L",
    color: "#ca8a04",
    soft: "rgba(240,216,79,0.22)",
    kcalPerG: 9,
  },
];

const MICRO_FIELDS = [
  ["fibra", "Fibra", "g"],
  ["azucarMeta", "Azúcares", "g"],
  ["grasaSaturada", "Grasa saturada", "g"],
  ["grasaMonosaturada", "Grasa monoinsat.", "g"],
  ["grasaPoliinsaturada", "Grasa poliinsat.", "g"],
  ["colesterolTotal", "Colesterol", "mg"],
  ["sodio", "Sodio", "mg"],
  ["potasio", "Potasio", "mg"],
  ["calcio", "Calcio", "mg"],
  ["hierro", "Hierro", "mg"],
  ["fosforo", "Fósforo", "mg"],
  ["selenio", "Selenio", "µg"],
  ["acidoFolico", "Ácido fólico", "µg"],
  ["vitaminaA", "Vitamina A", "µg"],
  ["acidoAscorbico", "Vitamina C", "mg"],
];

const hasValidImage = (value) => {
  if (!value || typeof value !== "string") return false;
  const trimmed = value.trim();
  return !!trimmed && trimmed.toLowerCase() !== "default";
};

const formatNumber = (value, decimals = 0) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0";
  const rounded =
    decimals > 0 ? num.toFixed(decimals).replace(/\.0+$/, "") : Math.round(num);
  return `${rounded}`;
};

const formatTiempo = (tiempo) => {
  if (!tiempo || typeof tiempo !== "object") return "";
  const horas = parseInt(tiempo.horas, 10) || 0;
  const minutos = parseInt(tiempo.minutos, 10) || 0;
  if (!horas && !minutos) return "";
  if (horas && minutos) return `${horas} h ${minutos} min`;
  if (horas) return `${horas} h`;
  return `${minutos} min`;
};

const capitalize = (value) =>
  typeof value === "string" && value.length
    ? value.charAt(0).toUpperCase() + value.slice(1)
    : "";

const formatDishDate = (value, withTime = false) => {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("es-MX", {
      dateStyle: "medium",
      ...(withTime ? { timeStyle: "short" } : {}),
    }).format(new Date(value));
  } catch (error) {
    return "";
  }
};

/* ── Card del listado ─────────────────────────────────────────────────── */

const DishCard = ({
  dish,
  accentColor,
  buttonGradient,
  buttonTextColor = "#fff",
  onOpenDish,
}) => {
  const nombre = dish?.nombre || "Platillo";
  const descripcion = dish?.detalles?.descripcion || "";
  const kcal = formatNumber(dish?.necesidades?.energiaKcal);
  const tiempo = formatTiempo(dish?.detalles?.tiempoPreparacion);
  const tiposComida = Array.isArray(dish?.detalles?.tipoComida)
    ? dish.detalles.tipoComida.filter(Boolean).slice(0, 2)
    : [];
  const likes = Number(dish?.numeroLikes) || 0;
  const comments = Number(dish?.numeroComentarios) || 0;
  const ingredientes = Number(dish?.numeroIngredientes) || 0;
  const imageOk = hasValidImage(dish?.imagen);

  const handleOpen = () => onOpenDish(dish);

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleOpen();
        }
      }}
      sx={{
        borderRadius: 4,
        border: "1px solid #e5e7eb",
        background: "#fff",
        boxShadow: "0 18px 50px rgba(15,23,42,0.08)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        cursor: "pointer",
        transition: "transform 240ms ease, box-shadow 240ms ease",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: `0 25px 55px ${withAlpha(accentColor, 0.22)}`,
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        {imageOk ? (
          <Box
            component="img"
            src={dish.imagen}
            alt={nombre}
            loading="lazy"
            sx={{ width: "100%", height: 190, objectFit: "cover" }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: 190,
              bgcolor: "#f1f5f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#94a3b8",
            }}
          >
            <RamenDiningIcon sx={{ fontSize: 72 }} />
          </Box>
        )}
        <Chip
          icon={
            <LocalFireDepartmentIcon
              sx={{ fontSize: 16, color: "#fbbf24 !important" }}
            />
          }
          label={`${kcal} kcal`}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "rgba(15,23,42,0.78)",
            color: "#f8fafc",
            fontWeight: 700,
            backdropFilter: "blur(6px)",
          }}
        />
        {(likes > 0 || comments > 0) && (
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{
              position: "absolute",
              bottom: 12,
              left: 12,
              background: "rgba(15,23,42,0.72)",
              borderRadius: 999,
              px: 1.5,
              py: 0.5,
              backdropFilter: "blur(6px)",
            }}
          >
            {likes > 0 && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <FavoriteIcon sx={{ fontSize: 14, color: "#fb7185" }} />
                <Typography
                  variant="caption"
                  sx={{ color: "#f8fafc", fontWeight: 600 }}
                >
                  {likes}
                </Typography>
              </Stack>
            )}
            {comments > 0 && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <CommentIcon sx={{ fontSize: 14, color: "#93c5fd" }} />
                <Typography
                  variant="caption"
                  sx={{ color: "#f8fafc", fontWeight: 600 }}
                >
                  {comments}
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
      </Box>

      <Box
        sx={{
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          flexGrow: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#0f172a",
            fontSize: 17,
            lineHeight: 1.35,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {nombre}
        </Typography>

        {descripcion && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {descripcion}
          </Typography>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {tiempo && (
            <Chip
              icon={<AccessTimeIcon sx={{ fontSize: 15 }} />}
              label={tiempo}
              size="small"
              sx={{ background: "#f1f5f9", fontWeight: 600 }}
            />
          )}
          {tiposComida.map((tipoComida) => (
            <Chip
              key={tipoComida}
              label={capitalize(tipoComida)}
              size="small"
              sx={{
                background: withAlpha(accentColor, 0.1),
                color: accentColor,
                fontWeight: 600,
              }}
            />
          ))}
          {ingredientes > 0 && (
            <Chip
              icon={<RestaurantMenuIcon sx={{ fontSize: 15 }} />}
              label={`${ingredientes} ingredientes`}
              size="small"
              sx={{ background: "#f8fafc", fontWeight: 600 }}
            />
          )}
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mt: "auto", pt: 0.5 }}>
          {MACROS.map((macro) => (
            <Stack
              key={macro.key}
              direction="row"
              spacing={0.6}
              alignItems="center"
            >
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  background: macro.color,
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: "#475569", fontWeight: 600 }}
              >
                {macro.short} {formatNumber(dish?.necesidades?.[macro.key])} g
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Button
          variant="contained"
          endIcon={<RestaurantMenuIcon fontSize="small" />}
          onClick={(event) => {
            event.stopPropagation();
            handleOpen();
          }}
          sx={{
            alignSelf: "center",
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            py: 1.1,
            borderRadius: 999,
            color: buttonTextColor,
            backgroundImage:
              buttonGradient ||
              `linear-gradient(120deg, ${accentColor} 0%, ${accentColor} 100%)`,
            boxShadow: `0 15px 35px ${withAlpha(accentColor, 0.3)}`,
            transition: "transform 200ms ease, box-shadow 200ms ease",
            "&:hover": {
              transform: "translateY(-2px)",
              backgroundImage:
                buttonGradient ||
                `linear-gradient(120deg, ${accentColor} 0%, ${accentColor} 100%)`,
              boxShadow: `0 20px 45px ${withAlpha(accentColor, 0.4)}`,
            },
          }}
        >
          Ver receta
        </Button>
      </Box>
    </Box>
  );
};

/* ── Listado con búsqueda y paginación (server-side) ──────────────────── */

const DishesShowcase = ({
  dishes,
  total,
  page,
  pageSize,
  onPageChange,
  search,
  onSearchChange,
  loading,
  onOpenDish,
  accentColor,
  buttonGradient,
  buttonTextColor = "#fff",
}) => {
  const totalPages = Math.max(1, Math.ceil((Number(total) || 0) / pageSize));
  const shouldShowSearch = (Number(total) || 0) > pageSize || !!search;

  return (
    <Stack spacing={3}>
      <Stack
        spacing={2}
        direction={{ xs: "column", lg: "row" }}
        alignItems={{ xs: "stretch", lg: "center" }}
        justifyContent="space-between"
        sx={{
          borderRadius: 4,
          border: "1px solid rgba(15,23,42,0.08)",
          background: "linear-gradient(120deg, rgba(248,250,252,0.95), #fff)",
          px: { xs: 2.5, md: 3.5 },
          py: { xs: 2.75, md: 2.5 },
        }}
      >
        <Stack direction="row" spacing={2.5} alignItems="center">
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              background: withAlpha(accentColor, 0.1),
              border: `1px solid ${withAlpha(accentColor, 0.22)}`,
              color: accentColor,
            }}
          >
            <RamenDiningIcon />
          </Box>
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 1.5 }}
            >
              Platillos compartidos
            </Typography>
            <Typography variant="h5" fontWeight={800} color="text.primary">
              Recetas que este nutriólogo comparte contigo
            </Typography>
          </Box>
        </Stack>
        {!!total && (
          <Chip
            label={`${total} ${total === 1 ? "receta" : "recetas"}`}
            sx={{ background: "#f1f5f9", fontWeight: 600, alignSelf: "center" }}
          />
        )}
      </Stack>

      {shouldShowSearch && (
        <Box sx={{ px: { xs: 2.5, md: 0 } }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Buscar receta por nombre"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 3,
                backgroundColor: "#fff",
              },
            }}
            sx={{ width: { xs: "100%", md: "75%" } }}
          />
        </Box>
      )}

      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
          <CircularProgress sx={{ color: accentColor }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Cargando platillos...
          </Typography>
        </Stack>
      ) : dishes.length ? (
        <Box sx={{ px: { xs: 2.5, md: 0 } }}>
          <Grid container spacing={3}>
            {dishes.map((dish) => (
              <Grid item xs={12} md={6} key={dish?._id || dish?.id}>
                <DishCard
                  dish={dish}
                  accentColor={accentColor}
                  buttonGradient={buttonGradient}
                  buttonTextColor={buttonTextColor}
                  onOpenDish={onOpenDish}
                />
              </Grid>
            ))}
            {totalPages > 1 && (
              <Grid item xs={12}>
                <Stack alignItems="center" sx={{ pt: 2 }}>
                  <BrandPagination
                    count={totalPages}
                    page={Math.min(page, totalPages)}
                    onChange={(_, value) => onPageChange(value)}
                    gradient={buttonGradient}
                    accentColor={accentColor}
                    textColor={buttonTextColor}
                  />
                </Stack>
              </Grid>
            )}
          </Grid>
        </Box>
      ) : (
        <Box sx={{ px: { xs: 2.5, md: 0 } }}>
          <Box
            sx={{
              borderRadius: 4,
              border: "1px dashed #cbd5f5",
              background: "rgba(248,250,255,0.6)",
              textAlign: "center",
              py: 6,
              px: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {search
                ? "No encontramos recetas con ese nombre"
                : "Aún no hay platillos compartidos"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {search
                ? "Intenta con otro término de búsqueda."
                : "Cuando este nutriólogo comparta recetas, las verás aquí."}
            </Typography>
          </Box>
        </Box>
      )}
    </Stack>
  );
};

/* ── Dialog de detalle (solo lectura) ─────────────────────────────────── */

const SectionTitle = ({ icon, title, accent, chipLabel, right }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    flexWrap="wrap"
    gap={1}
    sx={{ mb: 1.75 }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 2,
          display: "grid",
          placeItems: "center",
          background: withAlpha(accent, 0.12),
          color: accent,
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: 17 }}>
        {title}
      </Typography>
      {chipLabel != null && (
        <Chip
          label={chipLabel}
          size="small"
          sx={{ background: "#eef2f7", fontWeight: 700 }}
        />
      )}
    </Stack>
    {right}
  </Stack>
);

const DishDialog = ({
  open,
  dish,
  loading,
  onClose,
  gradient,
  accentColor,
  textOnGradient = "#fff",
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const ingredientes = useMemo(
    () => (Array.isArray(dish?.ingredientes) ? dish.ingredientes : []),
    [dish?.ingredientes]
  );
  const pasos = useMemo(
    () =>
      Array.isArray(dish?.detalles?.pasosReceta)
        ? dish.detalles.pasosReceta.filter(
            (paso) => typeof paso === "string" && paso.trim()
          )
        : [],
    [dish?.detalles?.pasosReceta]
  );
  const comentarios = useMemo(
    () => (Array.isArray(dish?.comentarios) ? dish.comentarios : []),
    [dish?.comentarios]
  );

  const macroData = useMemo(() => {
    const necesidades = dish?.necesidades || {};
    const entries = MACROS.map((macro) => {
      const grams = Number(necesidades[macro.key]) || 0;
      return { ...macro, grams, kcal: grams * macro.kcalPerG };
    });
    const totalKcal = entries.reduce((acc, entry) => acc + entry.kcal, 0);
    return entries.map((entry) => ({
      ...entry,
      pct: totalKcal > 0 ? (entry.kcal / totalKcal) * 100 : 0,
    }));
  }, [dish?.necesidades]);

  const microData = useMemo(() => {
    const necesidades = dish?.necesidades || {};
    return MICRO_FIELDS.map(([key, label, unit]) => ({
      key,
      label,
      unit,
      value: Number(necesidades[key]) || 0,
    })).filter((entry) => entry.value > 0);
  }, [dish?.necesidades]);

  if (!dish) return null;

  const nombre = dish?.nombre || "Platillo";
  const descripcion = dish?.detalles?.descripcion || "";
  const kcal = formatNumber(dish?.necesidades?.energiaKcal);
  const tiempo = formatTiempo(dish?.detalles?.tiempoPreparacion);
  const tiposComida = Array.isArray(dish?.detalles?.tipoComida)
    ? dish.detalles.tipoComida.filter(Boolean)
    : [];
  const likes = Number(dish?.numeroLikes) || 0;
  const imageOk = hasValidImage(dish?.imagen);
  const esDeSistema = dish?.tipo === "DE_SISTEMA";

  const glassChip = {
    background: "rgba(255,255,255,0.16)",
    color: "#fff",
    fontWeight: 600,
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(255,255,255,0.25)",
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 5,
          overflow: "hidden",
        },
      }}
    >
      {loading ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{ py: 12, px: 3, minHeight: 380 }}
        >
          <CircularProgress sx={{ color: accentColor }} />
          <Typography color="text.secondary">Cargando receta...</Typography>
        </Stack>
      ) : (
        <>
          {/* Hero con imagen y datos clave */}
          <Box sx={{ position: "relative", flexShrink: 0 }}>
            {imageOk ? (
              <Box
                component="img"
                src={dish.imagen}
                alt={nombre}
                sx={{
                  width: "100%",
                  height: { xs: 230, sm: 290 },
                  objectFit: "cover",
                  display: "block",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: { xs: 200, sm: 240 },
                  backgroundImage: gradient,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <RamenDiningIcon
                  sx={{ fontSize: 96, color: "rgba(255,255,255,0.85)" }}
                />
              </Box>
            )}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(2,6,23,0.08) 25%, rgba(2,6,23,0.85) 100%)",
              }}
            />
            <IconButton
              onClick={onClose}
              aria-label="Cerrar"
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                color: "#fff",
                background: "rgba(15,23,42,0.55)",
                backdropFilter: "blur(8px)",
                "&:hover": { background: "rgba(15,23,42,0.8)" },
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                p: { xs: 2.5, sm: 3.5 },
              }}
            >
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {esDeSistema && (
                    <Chip
                      icon={
                        <VerifiedIcon
                          sx={{ fontSize: 15, color: "#7dd3fc !important" }}
                        />
                      }
                      label="Receta Nutrilander"
                      size="small"
                      sx={glassChip}
                    />
                  )}
                  {tiposComida.map((tipoComida) => (
                    <Chip
                      key={tipoComida}
                      label={capitalize(tipoComida)}
                      size="small"
                      sx={glassChip}
                    />
                  ))}
                </Stack>
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: { xs: 22, sm: 30 },
                    lineHeight: 1.2,
                    textShadow: "0 6px 24px rgba(2,6,23,0.55)",
                  }}
                >
                  {nombre}
                </Typography>
                <Stack
                  direction="row"
                  spacing={2.25}
                  flexWrap="wrap"
                  useFlexGap
                  alignItems="center"
                >
                  <Stack direction="row" spacing={0.6} alignItems="center">
                    <LocalFireDepartmentIcon
                      sx={{ fontSize: 18, color: "#fbbf24" }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: "#f1f5f9", fontWeight: 700 }}
                    >
                      {kcal} kcal
                    </Typography>
                  </Stack>
                  {tiempo && (
                    <Stack direction="row" spacing={0.6} alignItems="center">
                      <AccessTimeIcon sx={{ fontSize: 17, color: "#a5b4fc" }} />
                      <Typography variant="body2" sx={{ color: "#e2e8f0" }}>
                        {tiempo}
                      </Typography>
                    </Stack>
                  )}
                  {!!ingredientes.length && (
                    <Stack direction="row" spacing={0.6} alignItems="center">
                      <RestaurantMenuIcon
                        sx={{ fontSize: 17, color: "#86efac" }}
                      />
                      <Typography variant="body2" sx={{ color: "#e2e8f0" }}>
                        {ingredientes.length} ingredientes
                      </Typography>
                    </Stack>
                  )}
                  {likes > 0 && (
                    <Stack direction="row" spacing={0.6} alignItems="center">
                      <FavoriteIcon sx={{ fontSize: 17, color: "#fb7185" }} />
                      <Typography variant="body2" sx={{ color: "#e2e8f0" }}>
                        {likes}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Box>
          </Box>

          <DialogContent sx={{ background: "#f8fafc", p: { xs: 2.5, sm: 4 } }}>
            <Stack spacing={3.5}>
              {descripcion && (
                <Typography
                  sx={{ color: "#334155", lineHeight: 1.7, fontSize: 15.5 }}
                >
                  {descripcion}
                </Typography>
              )}

              {/* Información nutrimental */}
              <Box>
                <SectionTitle
                  icon={<DonutLargeIcon sx={{ fontSize: 19 }} />}
                  title="Información nutrimental"
                  accent={accentColor}
                  right={
                    <Chip
                      icon={
                        <LocalFireDepartmentIcon
                          sx={{ fontSize: 15, color: "#d97706 !important" }}
                        />
                      }
                      label={`${kcal} kcal totales`}
                      size="small"
                      sx={{
                        background: "rgba(245,158,11,0.12)",
                        color: "#b45309",
                        fontWeight: 700,
                      }}
                    />
                  }
                />
                <Box
                  sx={{
                    background: "#fff",
                    borderRadius: 3,
                    border: "1px solid rgba(15,23,42,0.06)",
                    p: { xs: 2, sm: 2.5 },
                  }}
                >
                  {/* Barra de distribución calórica */}
                  <Box
                    sx={{
                      display: "flex",
                      height: 12,
                      borderRadius: 999,
                      overflow: "hidden",
                      background: "#f1f5f9",
                    }}
                  >
                    {macroData.map(
                      (macro) =>
                        macro.pct > 0 && (
                          <Box
                            key={macro.key}
                            sx={{
                              width: `${Math.max(macro.pct, 2)}%`,
                              background: macro.color,
                              transition: "width 600ms ease",
                            }}
                          />
                        )
                    )}
                  </Box>
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    {macroData.map((macro) => (
                      <Grid item xs={12} sm={4} key={macro.key}>
                        <Stack
                          direction="row"
                          spacing={1.25}
                          alignItems="center"
                          sx={{
                            borderRadius: 2.5,
                            background: macro.soft,
                            px: 1.75,
                            py: 1.25,
                          }}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              background: macro.color,
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#64748b",
                                fontWeight: 600,
                                display: "block",
                                lineHeight: 1.2,
                              }}
                            >
                              {macro.label}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 800, color: "#0f172a" }}
                            >
                              {formatNumber(macro.grams, 1)} g ·{" "}
                              {formatNumber(macro.pct)}%
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>

              {/* Ingredientes */}
              {!!ingredientes.length && (
                <Box>
                  <SectionTitle
                    icon={<RestaurantMenuIcon sx={{ fontSize: 19 }} />}
                    title="Ingredientes"
                    accent={accentColor}
                    chipLabel={ingredientes.length}
                  />
                  <Box
                    sx={{
                      background: "#fff",
                      borderRadius: 3,
                      border: "1px solid rgba(15,23,42,0.06)",
                      px: { xs: 2, sm: 2.5 },
                      py: 0.5,
                    }}
                  >
                    {ingredientes.map((ingrediente, index) => (
                      <Stack
                        key={ingrediente?._id || `${ingrediente?.nombre}-${index}`}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{
                          py: 1.5,
                          borderBottom:
                            index === ingredientes.length - 1
                              ? "none"
                              : "1px dashed rgba(15,23,42,0.08)",
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: accentColor,
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "#1e293b" }}
                            >
                              {ingrediente?.nombre || "Ingrediente"}
                            </Typography>
                            {ingrediente?.categoriaTexto && (
                              <Typography
                                variant="caption"
                                sx={{ color: "#94a3b8" }}
                              >
                                {ingrediente.categoriaTexto}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                        <Stack alignItems="flex-end" sx={{ flexShrink: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: "#0f172a" }}
                          >
                            {ingrediente?.cantidadString ||
                              formatNumber(ingrediente?.cantidad, 2)}{" "}
                            {ingrediente?.unidad || ""}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "#94a3b8" }}
                          >
                            {formatNumber(ingrediente?.energiaKcal)} kcal
                          </Typography>
                        </Stack>
                      </Stack>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Preparación */}
              {!!pasos.length && (
                <Box>
                  <SectionTitle
                    icon={<SoupKitchenIcon sx={{ fontSize: 19 }} />}
                    title="Preparación"
                    accent={accentColor}
                    right={
                      tiempo ? (
                        <Chip
                          icon={<AccessTimeIcon sx={{ fontSize: 15 }} />}
                          label={tiempo}
                          size="small"
                          sx={{ background: "#f1f5f9", fontWeight: 600 }}
                        />
                      ) : null
                    }
                  />
                  <Stack
                    spacing={0}
                    sx={{
                      background: "#fff",
                      borderRadius: 3,
                      border: "1px solid rgba(15,23,42,0.06)",
                      px: { xs: 2, sm: 2.5 },
                      py: 1,
                    }}
                  >
                    {pasos.map((paso, index) => (
                      <Stack
                        key={`paso-${index}`}
                        direction="row"
                        spacing={2}
                        alignItems="flex-start"
                        sx={{
                          py: 1.5,
                          borderBottom:
                            index === pasos.length - 1
                              ? "none"
                              : "1px dashed rgba(15,23,42,0.08)",
                        }}
                      >
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            backgroundImage: gradient,
                            color: textOnGradient,
                            fontWeight: 800,
                            fontSize: 14,
                            boxShadow: `0 8px 18px ${withAlpha(
                              accentColor,
                              0.3
                            )}`,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ color: "#334155", lineHeight: 1.7, pt: 0.5 }}
                        >
                          {paso}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Micronutrientes (colapsable) */}
              {!!microData.length && (
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    background: "#fff",
                    borderRadius: "12px !important",
                    border: "1px solid rgba(15,23,42,0.06)",
                    "&::before": { display: "none" },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 700, color: "#334155" }}>
                      Más información nutrimental
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Grid container spacing={1.5}>
                      {microData.map((entry) => (
                        <Grid item xs={6} sm={4} key={entry.key}>
                          <Box
                            sx={{
                              borderRadius: 2,
                              background: "#f8fafc",
                              px: 1.5,
                              py: 1.25,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#64748b", display: "block" }}
                            >
                              {entry.label}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: "#0f172a" }}
                            >
                              {formatNumber(entry.value, 1)} {entry.unit}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Comentarios (solo lectura) */}
              {!!comentarios.length && (
                <Box>
                  <SectionTitle
                    icon={<CommentIcon sx={{ fontSize: 18 }} />}
                    title="Comentarios"
                    accent={accentColor}
                    chipLabel={comentarios.length}
                  />
                  <Stack spacing={1.5}>
                    {comentarios
                      .slice()
                      .sort((a, b) => new Date(b?.fecha) - new Date(a?.fecha))
                      .map((comentario, index) => (
                        <Box
                          key={comentario?._id || `comentario-${index}`}
                          sx={{
                            borderRadius: 3,
                            border: "1px solid #e2e8f0",
                            background: "#fff",
                            p: 2,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700 }}
                          >
                            {comentario?.nutriologoId?.nombreAMostrar ||
                              `${comentario?.nutriologoId?.nombre || ""} ${
                                comentario?.nutriologoId?.apellidos || ""
                              }`.trim() ||
                              "Nutriólogo"}
                          </Typography>
                          {formatDishDate(comentario?.fecha, true) && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatDishDate(comentario?.fecha, true)}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {comentario?.comentario}
                          </Typography>
                        </Box>
                      ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </DialogContent>

          <Divider />
          <DialogActions sx={{ px: 3, py: 1.75, background: "#fff" }}>
            <Button
              onClick={onClose}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Cerrar
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export { DishCard, DishDialog };
export default DishesShowcase;
